import type { CSSProperties, FunctionComponent } from 'react';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import Button from '@atlaskit/button';
import Checkbox from '@atlaskit/checkbox';
import Flag from '@atlaskit/flag';
import Heading from '@atlaskit/heading';
import CheckCircleOutlineIcon from '@atlaskit/icon/core/check-circle';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import InfoIcon from '@atlaskit/icon/core/information';
import Lozenge from '@atlaskit/lozenge/new';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
  ReactConsentContext,
  useInfo,
  useToken,
} from '../../../Flow/ConsentContextProvider';
import { TWG_FOOTER_MIN_HEIGHT } from '../TwgFooter';
import {
  HIDDEN_TWG_SCOPES,
  TWG_SCOPE_GROUPS,
  SCOPE_BY_VALUE,
} from './scope-catalog';
import type { TwgScopeGroup, TwgScopeEntry } from './scope-catalog';

const messages = defineMessages({
  sectionTitle: {
    id: 'oauth.twg.permissions.sectionTitle',
    defaultMessage: 'Permissions',
    description: 'Section heading for TWG permissions',
  },
  disabledByOrgAdmin: {
    id: 'oauth.twg.permissions.disabledByOrgAdmin',
    defaultMessage: 'Disabled by org admin',
    description:
      'Message shown beside a TWG scope that an organization admin has disabled',
  },
  editPermissions: {
    id: 'oauth.twg.permissions.editPermissions',
    defaultMessage: 'Edit permissions',
    description:
      'Button shown below TWG permission groups to let users choose individual permissions',
  },
  limitedPermissionsTitle: {
    id: 'oauth.twg.permissions.limitedPermissionsTitle',
    defaultMessage:
      'Unselected permissions will limit Teamwork Graph CLI features',
    description:
      'Title for the warning flag shown when users deselect TWG CLI permissions',
  },
  limitedPermissionsBody: {
    id: 'oauth.twg.permissions.limitedPermissionsBody',
    defaultMessage:
      "If you later want to use a feature that requires a permission you didn't grant, you'll need to disconnect and reconnect Teamwork Graph CLI to update your permissions.",
    description:
      'Body for the warning flag shown when users deselect TWG CLI permissions',
  },
  limitedPermissionsDismiss: {
    id: 'oauth.twg.permissions.limitedPermissionsDismiss',
    defaultMessage: 'Understood',
    description:
      'Button to dismiss the warning flag shown when users deselect TWG CLI permissions',
  },
});

const warningFlagContainerStyle = xcss({
  position: 'fixed',
  bottom: `calc(${TWG_FOOTER_MIN_HEIGHT} + var(--ds-space-400, 32px))`,
  left: 'space.400',
  // @ts-expect-error static z-index
  zIndex: 100,
  width: '400px',
  maxWidth: '90vw',
  backgroundColor: 'color.background.warning',
  borderRadius: 'radius.small',
  boxShadow: 'elevation.shadow.overlay',
});

const styles: Record<string, CSSProperties> = {
  permissionsRoot: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: '100%',
  },
  groups: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
  },
  groupCard: {
    backgroundColor: 'var(--twg-card-bg)',
    borderColor: 'var(--twg-card-border)',
    borderRadius: '8px',
    borderStyle: 'solid',
    borderWidth: '1px',
    boxSizing: 'border-box',
    color: 'var(--twg-card-text)',
    overflow: 'hidden',
    width: '100%',
  },
  groupSummary: {
    paddingBlock: '19px',
    paddingInline: '20px 24px',
  },
  groupSummaryInner: {
    alignItems: 'flex-start',
    display: 'flex',
    gap: '20px',
    justifyContent: 'space-between',
    width: '100%',
  },
  groupCopy: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0,
  },
  groupExpandButton: {
    alignItems: 'center',
    backgroundColor: 'transparent',
    border: 0,
    color: 'inherit',
    cursor: 'pointer',
    display: 'flex',
    flex: '1 1 auto',
    font: 'inherit',
    gap: '20px',
    justifyContent: 'space-between',
    minWidth: 0,
    padding: 0,
    textAlign: 'left',
    width: '100%',
  },
  groupHeading: {
    color: 'var(--twg-card-text)',
    fontSize: '16px',
    fontWeight: 700,
    lineHeight: '20px',
  },
  groupDescription: {
    color: 'var(--twg-card-text)',
    fontSize: '14px',
    lineHeight: '20px',
    margin: 0,
  },
  collapsedIcon: {
    color: 'var(--twg-card-text)',
    flexShrink: 0,
    paddingTop: '0',
  },
  checkboxCell: {
    flexShrink: 0,
    paddingTop: '0',
  },
  chevronCell: {
    color: 'var(--twg-card-muted)',
    flexShrink: 0,
    marginLeft: 'auto',
  },
  scopeList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    paddingBlockEnd: '20px',
    paddingInlineEnd: '28px',
    paddingInlineStart: '76px',
  },
  scopeRow: {
    alignItems: 'flex-start',
    borderRadius: '6px',
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'flex',
    gap: '14px',
    marginInlineStart: '-4px',
    paddingBlock: '6px',
    paddingInline: '4px',
  },
  scopeCheckboxCell: {
    flexShrink: 0,
    paddingTop: 0,
  },
  scopeRowDisabled: {
    cursor: 'not-allowed',
    opacity: 0.62,
  },
  scopeCopy: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    gap: '6px',
    minWidth: 0,
  },
  scopeLabel: {
    color: 'var(--twg-card-text)',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
  },
  scopeTitleLine: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  scopeDescription: {
    color: 'var(--twg-card-muted)',
    fontSize: '14px',
    lineHeight: '20px',
  },
  editButton: {
    marginTop: '16px',
  },
};

type ScopeGroupCardProps = {
  group: TwgScopeGroup;
  allInGroup: TwgScopeEntry[];
  enabledGroupScopeValues: string[];
  allSelected: boolean;
  editMode: boolean;
  isIndeterminate: boolean;
  selectedScopesSet: ReadonlySet<string>;
  allowedScopesSet: ReadonlySet<string>;
  expanded: boolean;
  onToggleExpanded: () => void;
  onToggleScopes: (scopes: readonly string[], checked: boolean) => void;
  onToggleGroupAll: (groupScopes: string[], checked: boolean) => void;
};

const ScopeGroupCard: FunctionComponent<ScopeGroupCardProps> = ({
  group,
  allInGroup,
  enabledGroupScopeValues,
  allSelected,
  editMode,
  isIndeterminate,
  selectedScopesSet,
  allowedScopesSet,
  expanded,
  onToggleExpanded,
  onToggleScopes,
  onToggleGroupAll,
}) => {
  return (
    <div style={styles.groupCard}>
      <div style={styles.groupSummary}>
        <div style={styles.groupSummaryInner}>
          {editMode ? (
            <span style={styles.checkboxCell}>
              <Checkbox
                isChecked={allSelected}
                isIndeterminate={isIndeterminate}
                isDisabled={enabledGroupScopeValues.length === 0}
                onChange={(event) =>
                  onToggleGroupAll(
                    enabledGroupScopeValues,
                    event.currentTarget.checked,
                  )
                }
                name={`group-${group.id}`}
                label=""
                aria-label={`Select all ${group.heading} permissions`}
              />
            </span>
          ) : (
            <span style={styles.collapsedIcon} aria-hidden="true">
              <CheckCircleOutlineIcon label="" size="medium" />
            </span>
          )}
          {editMode ? (
            <button
              type="button"
              style={styles.groupExpandButton}
              aria-expanded={expanded}
              onClick={onToggleExpanded}
            >
              <span style={styles.groupCopy}>
                <span style={styles.groupHeading}>{group.heading}</span>
                <span style={styles.groupDescription}>
                  {group.groupDescription}
                </span>
              </span>
              <span style={styles.chevronCell} aria-hidden="true">
                {expanded ? (
                  <ChevronUpIcon label="" size="small" />
                ) : (
                  <ChevronDownIcon label="" size="small" />
                )}
              </span>
            </button>
          ) : (
            <span style={styles.groupCopy}>
              <span style={styles.groupHeading}>{group.heading}</span>
              <span style={styles.groupDescription}>
                {group.groupDescription}
              </span>
            </span>
          )}
        </div>
      </div>
      {editMode && expanded && (
        <div style={styles.scopeList}>
          {allInGroup.map((entry) => {
            const enabledEntryScopeValues = entry.scopes.filter((scope) =>
              allowedScopesSet.has(scope),
            );
            const selectedEnabledEntryScopeValues =
              enabledEntryScopeValues.filter((scope) =>
                selectedScopesSet.has(scope),
              );
            const isAllowed = enabledEntryScopeValues.length > 0;
            const isSelected = selectedEnabledEntryScopeValues.length > 0;
            const isIndeterminate =
              selectedEnabledEntryScopeValues.length > 0 &&
              selectedEnabledEntryScopeValues.length <
                enabledEntryScopeValues.length;

            return (
              <div
                key={entry.id}
                onClick={(event) => {
                  if (
                    isAllowed &&
                    !(event.target instanceof HTMLInputElement)
                  ) {
                    onToggleScopes(enabledEntryScopeValues, !isSelected);
                  }
                }}
                style={{
                  ...styles.scopeRow,
                  ...(!isAllowed ? styles.scopeRowDisabled : {}),
                }}
              >
                <span style={styles.scopeCheckboxCell}>
                  <Checkbox
                    aria-label={entry.label}
                    isChecked={isSelected}
                    isIndeterminate={isIndeterminate}
                    isDisabled={!isAllowed}
                    onChange={(event) =>
                      onToggleScopes(
                        enabledEntryScopeValues,
                        event.currentTarget.checked,
                      )
                    }
                    name={entry.id}
                    label=""
                  />
                </span>
                <span style={styles.scopeCopy}>
                  <span style={styles.scopeTitleLine}>
                    <span style={styles.scopeLabel}>{entry.label}</span>
                    {!isAllowed && (
                      <span>
                        <Lozenge appearance="default" isBold>
                          <FormattedMessage {...messages.disabledByOrgAdmin} />
                        </Lozenge>
                      </span>
                    )}
                  </span>
                  {entry.description && (
                    <span style={styles.scopeDescription}>
                      {entry.description}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const isUnknownScopeInGroup = (scope: string, groupId: TwgScopeGroup['id']) => {
  if (groupId === 'write') return scope.startsWith('write:');
  if (groupId === 'delete') return scope.startsWith('delete:');
  return !scope.startsWith('write:') && !scope.startsWith('delete:');
};

interface PermissionsBulletsProps {
  defaultEditMode?: boolean;
  editMode?: boolean;
  onEditModeChange?: (editMode: boolean) => void;
}

const PermissionsBullets: FunctionComponent<PermissionsBulletsProps> = ({
  defaultEditMode = false,
  editMode: controlledEditMode,
  onEditModeChange,
}) => {
  const { scopes } = useToken();
  const { filteredScopes, disabledScopes } = useInfo();
  const context = useContext(ReactConsentContext);
  const { formatMessage } = useIntl();
  const [uncontrolledEditMode, setUncontrolledEditMode] =
    useState(defaultEditMode);
  const editMode = controlledEditMode ?? uncontrolledEditMode;
  const setEditMode = useCallback(
    (nextEditMode: boolean) => {
      if (controlledEditMode === undefined) {
        setUncontrolledEditMode(nextEditMode);
      }
      onEditModeChange?.(nextEditMode);
    },
    [controlledEditMode, onEditModeChange],
  );
  const [isWarningDismissed, setIsWarningDismissed] = useState(false);
  // Lifted expand state, keyed by group.id so it survives scope selection re-renders.
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    { view: true },
  );
  const toggleExpanded = useCallback(
    (groupId: string) =>
      setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] })),
    [],
  );

  // Stable per-group expand callbacks so ScopeGroupCard props don't change on scope selection re-renders.
  const groupToggleCallbacks = useMemo(
    () =>
      Object.fromEntries(
        TWG_SCOPE_GROUPS.map((g) => [g.id, () => toggleExpanded(g.id)]),
      ),
    [toggleExpanded],
  );
  const userSelectedScopes: string[] = context?.selectedScopes ?? [];
  const setSelectedScopes = context?.setSelectedScopes;
  const setAcceptDisabled = context?.setAcceptDisabled;

  const requestedScopesSet = useMemo(
    () =>
      new Set(
        [
          ...scopes,
          ...(filteredScopes ?? []),
          ...(disabledScopes ?? []),
        ].filter((scope) => !HIDDEN_TWG_SCOPES.has(scope)),
      ),
    [disabledScopes, filteredScopes, scopes],
  );

  const allowedScopesSet = useMemo(
    () =>
      new Set(
        (filteredScopes ?? scopes).filter((scope) => !HIDDEN_TWG_SCOPES.has(scope)),
      ),
    [filteredScopes, scopes],
  );

  const selectedScopesSet = useMemo(
    () => new Set(userSelectedScopes),
    [userSelectedScopes],
  );

  const enabledSelectedScopes = useMemo(
    () =>
      Array.from(allowedScopesSet).filter((scope) =>
        selectedScopesSet.has(scope),
      ),
    [allowedScopesSet, selectedScopesSet],
  );
  const showLimitedPermissionsWarning =
    editMode &&
    !isWarningDismissed &&
    allowedScopesSet.size > 0 &&
    enabledSelectedScopes.length < allowedScopesSet.size;

  // Use a ref to hold setAcceptDisabled so the effect dependency doesn't change
  // on every context re-render (context spreads a new object each time).
  const setAcceptDisabledRef = useRef(setAcceptDisabled);
  useEffect(() => {
    setAcceptDisabledRef.current = setAcceptDisabled;
  });

  useEffect(() => {
    setAcceptDisabledRef.current?.(enabledSelectedScopes.length === 0);
    return () => setAcceptDisabledRef.current?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledSelectedScopes.length]);

  const toggleScopes = useCallback(
    (scopesToToggle: readonly string[], checked: boolean) => {
      const enabledScopesToToggle = scopesToToggle.filter((scope) =>
        allowedScopesSet.has(scope),
      );
      if (!setSelectedScopes || enabledScopesToToggle.length === 0) return;
      setSelectedScopes(
        checked
          ? Array.from(
              new Set([...userSelectedScopes, ...enabledScopesToToggle]),
            )
          : userSelectedScopes.filter(
              (scope) => !enabledScopesToToggle.includes(scope),
            ),
      );
    },
    [allowedScopesSet, setSelectedScopes, userSelectedScopes],
  );

  const toggleGroupAll = useCallback(
    (groupScopes: string[], checked: boolean) => {
      if (!setSelectedScopes) return;
      if (checked) {
        const toAdd = groupScopes.filter((s) => !selectedScopesSet.has(s));
        setSelectedScopes(
          Array.from(new Set([...userSelectedScopes, ...toAdd])),
        );
      } else {
        const groupSet = new Set(groupScopes);
        setSelectedScopes(userSelectedScopes.filter((s) => !groupSet.has(s)));
      }
    },
    [setSelectedScopes, userSelectedScopes, selectedScopesSet],
  );

  return (
    <div style={styles.permissionsRoot}>
      <Heading size="small">
        <FormattedMessage {...messages.sectionTitle} />
      </Heading>
      <div style={styles.groups}>
        {TWG_SCOPE_GROUPS.map((group) => {
          const knownInGroup: TwgScopeEntry[] = group.scopes.filter((e) =>
            e.scopes.some((scope) => requestedScopesSet.has(scope)),
          );
          const unknownInGroup = Array.from(requestedScopesSet)
            .filter(
              (s) =>
                !SCOPE_BY_VALUE.has(s) && isUnknownScopeInGroup(s, group.id),
            )
            .map(
              (s) =>
                ({
                  id: `unknown-${s}`,
                  scopes: [s],
                  label: s,
                  description: '',
                }) satisfies TwgScopeEntry,
            );

          const allInGroup = [...knownInGroup, ...unknownInGroup];
          if (allInGroup.length === 0) return null;

          const groupScopeValues = Array.from(
            new Set(allInGroup.flatMap((e) => e.scopes)),
          );
          const enabledGroupScopeValues = groupScopeValues.filter((s) =>
            allowedScopesSet.has(s),
          );
          const selectedEnabledInGroup = enabledGroupScopeValues.filter((s) =>
            selectedScopesSet.has(s),
          );
          const allSelected =
            enabledGroupScopeValues.length > 0 &&
            selectedEnabledInGroup.length === enabledGroupScopeValues.length;
          const noneSelected = selectedEnabledInGroup.length === 0;
          const isIndeterminate = !allSelected && !noneSelected;

          return (
            <ScopeGroupCard
              key={group.id}
              group={group}
              allInGroup={allInGroup}
              enabledGroupScopeValues={enabledGroupScopeValues}
              allSelected={allSelected}
              editMode={editMode}
              isIndeterminate={isIndeterminate}
              selectedScopesSet={selectedScopesSet}
              allowedScopesSet={allowedScopesSet}
              expanded={!!expandedGroups[group.id]}
              onToggleExpanded={groupToggleCallbacks[group.id]}
              onToggleScopes={toggleScopes}
              onToggleGroupAll={toggleGroupAll}
            />
          );
        })}
      </div>
      {!editMode && (
        <div style={styles.editButton}>
          <Button
            appearance="link"
            spacing="none"
            type="button"
            onClick={() => setEditMode(true)}
          >
            <FormattedMessage {...messages.editPermissions} />
          </Button>
        </div>
      )}
      {showLimitedPermissionsWarning && (
        <Box xcss={warningFlagContainerStyle}>
          <Flag
            id="twg-limited-permissions-warning"
            icon={<InfoIcon label="" color={token('color.icon.information')} />}
            title={formatMessage(messages.limitedPermissionsTitle)}
            description={formatMessage(messages.limitedPermissionsBody)}
            onDismissed={() => setIsWarningDismissed(true)}
            actions={[
              {
                content: formatMessage(messages.limitedPermissionsDismiss),
                onClick: () => setIsWarningDismissed(true),
              },
            ]}
          />
        </Box>
      )}
    </div>
  );
};

export default PermissionsBullets;
